/**
 * Payable-day tally panel — opens when HR clicks a Payable Days number.
 *
 * Always shows the full month tally (every bucket + both equations) and the day-by-day grid.
 * The red "does not match — reason & how to fix" block appears ONLY when the days do not add
 * up to the month. HR can select ANY day (grid cell or unaccounted chip) and decide what it
 * means: full-day present, half day (0.5 off the leave balance), LWP, absent, holiday or
 * weekly off — so even the cases the system cannot judge end up accounted for.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import {
  CheckCircle2, AlertTriangle, Wrench, CalendarDays, Loader2, RotateCcw, MousePointerClick,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API_URL } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';

const headers = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

const CODE_STYLE = {
  P: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  HP: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  L: 'bg-blue-100 text-blue-800 border-blue-200',
  HL: 'bg-blue-50 text-blue-700 border-blue-200',
  LWP: 'bg-red-100 text-red-800 border-red-200',
  HLWP: 'bg-red-50 text-red-700 border-red-200',
  WO: 'bg-slate-100 text-slate-600 border-slate-200',
  HO: 'bg-amber-100 text-amber-800 border-amber-200',
  A: 'bg-rose-200 text-rose-900 border-rose-300',
  HA: 'bg-rose-100 text-rose-800 border-rose-200',
  '—': 'bg-slate-50 text-slate-400 border-dashed border-slate-300',
};

const ACTIONS = [
  { key: 'present', label: 'Full-day Present', cls: 'border-emerald-400 text-emerald-800 hover:bg-emerald-50' },
  { key: 'leave', label: 'Full-day Leave (paid)', cls: 'border-blue-500 text-blue-900 hover:bg-blue-50' },
  { key: 'half_day', label: '½ Present + ½ Leave', cls: 'border-blue-400 text-blue-800 hover:bg-blue-50' },
  { key: 'half_day_lwp', label: '½ Present + ½ LWP', cls: 'border-orange-400 text-orange-800 hover:bg-orange-50' },
  { key: 'half_leave_lwp', label: '½ Leave + ½ LWP', cls: 'border-purple-400 text-purple-800 hover:bg-purple-50' },
  { key: 'lwp', label: 'Full-day LWP', cls: 'border-red-400 text-red-800 hover:bg-red-50' },
  { key: 'absent', label: 'Full-day Absent', cls: 'border-rose-400 text-rose-800 hover:bg-rose-50' },
  { key: 'holiday', label: 'Holiday', cls: 'border-amber-400 text-amber-800 hover:bg-amber-50' },
  { key: 'weekly_off', label: 'Weekly Off', cls: 'border-slate-400 text-slate-700 hover:bg-slate-100' },
  { key: 'working_day', label: 'Working Day', cls: 'border-teal-400 text-teal-700 hover:bg-teal-50' },
  { key: 'clear', label: 'Revert to system', cls: 'border-slate-300 text-slate-600 hover:bg-slate-100' },
];

const Cell = ({ label, value, tone = 'text-slate-800' }) => (
  <div className="flex items-baseline gap-1">
    <span className="text-slate-500">{label}:</span>
    <strong className={tone}>{value}</strong>
  </div>
);

export function PayableTallyPanel({ row, entityId, month, locked, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [showDays, setShowDays] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveType, setLeaveType] = useState('Casual Leave');
  const [reason, setReason] = useState('');
  const [balReload, setBalReload] = useState(0);

  const legacy = row.accounted_days === undefined || row.accounted_days === null;
  const codes = useMemo(() => (row.day_codes || '').split('|').filter(Boolean), [row.day_codes]);
  const dates = row.unexplained_dates || [];
  const manual = useMemo(() => new Set(row.manual_dates || []), [row.manual_dates]);

  useEffect(() => { setSelected(new Set()); }, [row.employee_id, month]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/leaves/balance/${row.employee_id}`,
          { headers: headers(), credentials: 'include' });
        const d = await safeJson(res);
        if (!alive || !Array.isArray(d)) return;
        setLeaveTypes(d);
        if (!d.some(b => b.leave_type === 'Casual Leave') && d.length) setLeaveType(d[0].leave_type);
      } catch { /* the dialog still works with the default type */ }
    })();
    return () => { alive = false; };
  }, [row.employee_id, balReload]);

  const toggle = useCallback((iso) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(iso)) next.delete(iso); else next.add(iso);
      return next;
    });
  }, []);

  const run = async (action) => {
    const list = [...selected].sort();
    if (!list.length) {
      toast.error('Select the date(s) first — tap them in the day-by-day grid.');
      return;
    }
    if (['leave', 'half_day', 'half_leave_lwp'].includes(action) && !leaveType) {
      toast.error('Pick the leave type this leave should be deducted from.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/attendance/monthly-approval/${entityId}/${month}/day-action`, {
        method: 'POST', headers: headers(), credentials: 'include',
        body: JSON.stringify({
          action, dates: list, employee_id: row.employee_id,
          leave_type: leaveType, reason: reason.trim() || undefined,
        }),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Could not save the decision');
      toast.success(d.message);
      (d.blocked || []).forEach(b => toast.error(`${b.date}: ${b.reason}`));
      setSelected(new Set());
      setReason('');
      setBalReload((n) => n + 1);   // refresh the (bal N) labels after a balance change
      onChanged?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 py-1" data-testid={`tally-panel-${row.employee_id}`}>
      <p className="font-semibold text-slate-800">
        {row.employee_name} — how {row.payable_days} net payable day(s) were calculated
      </p>

      {legacy ? (
        <div className="rounded border border-amber-300 bg-amber-50 p-2 text-amber-900" data-testid="tally-legacy-note">
          This locked snapshot was created before the new day tally. Click <strong>Recalculate</strong> (or
          Unlock → Lock &amp; Approve) to rebuild it with the full day-by-day breakdown.
        </div>
      ) : (
        <>
          <div className="rounded-md border border-slate-200 bg-white p-2.5 space-y-1 font-mono text-[11px]">
            <p data-testid={`tally-eq-${row.employee_id}`}>{row.tally_equation}</p>
            <p className="text-[#DC2626] font-semibold" data-testid={`payable-eq-${row.employee_id}`}>
              {row.payable_equation}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1">
            <Cell label="Days in month" value={row.month_days} />
            <Cell label="Present (worked)" value={row.present_days} tone="text-emerald-700" />
            <Cell label="Approved paid leave" value={row.approved_leave_days} tone="text-blue-700" />
            <Cell label="Leave without pay" value={row.lwp_days ?? 0} tone="text-red-700" />
            <Cell label="Weekly offs" value={row.week_off_days ?? 0} />
            <Cell label="Holidays (HR marked)" value={row.paid_holidays ?? 0} tone="text-amber-700" />
            <Cell label="Absent (unexplained)" value={row.absent_days ?? 0} tone="text-rose-700" />
            <Cell label="Late marks" value={`${row.late_marks} (−${row.late_deduction || 0} day)`} />
          </div>

          {row.leave_by_type && Object.keys(row.leave_by_type).length > 0 && (
            <div className="flex flex-wrap items-center gap-1" data-testid={`leave-split-${row.employee_id}`}>
              <span className="text-slate-500">Leave split by category:</span>
              {Object.entries(row.leave_by_type).map(([t, d]) => (
                <Badge key={t} variant="outline" className="text-[10px]">{t}: {d}</Badge>
              ))}
            </div>
          )}

          {row.worked_on_off_days > 0 && (
            <p className="text-[11px] text-slate-500">
              {row.worked_on_off_days} day(s) were worked on a weekly off / holiday — already paid as an off
              day. Raise a Comp-Off request to compensate the extra work.
            </p>
          )}

          {row.tally_ok ? (
            <div className="rounded border border-emerald-300 bg-emerald-50 p-2 text-emerald-900 flex items-start gap-2"
              data-testid={`tally-ok-${row.employee_id}`}>
              <CheckCircle2 className="w-4 h-4 mt-0.5" />
              <span>
                <strong>Tally OK.</strong> All {row.month_days} days of {month} are accounted for and the net
                payable days match. Nothing to fix.
              </span>
            </div>
          ) : (
            <div className="rounded border border-red-300 bg-red-50 p-2.5 text-red-900 space-y-2"
              data-testid={`tally-mismatch-${row.employee_id}`}>
              <p className="font-semibold flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" /> Days do not match — why
              </p>
              <ul className="list-disc ml-5 space-y-0.5">
                {(row.mismatch_reasons || []).map((r, i) => <li key={i}>{r}</li>)}
              </ul>
              {(row.fix_steps || []).length > 0 && (
                <>
                  <p className="font-semibold flex items-center gap-1.5 pt-1">
                    <Wrench className="w-4 h-4" /> Steps to make it match
                  </p>
                  <ol className="list-decimal ml-5 space-y-0.5">
                    {row.fix_steps.map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                </>
              )}
              {dates.length > 0 && (
                <div className="bg-white/70 rounded border border-red-200 p-2 space-y-1.5">
                  <p className="text-[11px] text-slate-600">
                    Unaccounted dates — tap to select, then choose an action below:
                  </p>
                  <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                    {dates.map((d) => (
                      <button key={d} type="button" onClick={() => toggle(d)}
                        data-testid={`fix-date-${d}`} aria-pressed={selected.has(d)}
                        className={`px-2 py-0.5 rounded border text-[10px] font-mono transition-colors ${
                          selected.has(d)
                            ? 'bg-[#DC2626] border-[#DC2626] text-white'
                            : 'bg-white border-slate-300 text-slate-600 hover:border-[#DC2626]'
                        }`}>
                        {d.slice(8)}/{d.slice(5, 7)}
                      </button>
                    ))}
                    {dates.length > 1 && (
                      <button type="button" onClick={() => setSelected(new Set(dates))}
                        data-testid={`select-all-unaccounted-${row.employee_id}`}
                        className="px-2 py-0.5 rounded border border-slate-400 text-[10px] text-slate-700 hover:bg-slate-100">
                        select all {dates.length}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---- HR decision bar: works for ANY selected day ---- */}
          <div className="rounded-md border border-slate-300 bg-white p-2.5 space-y-2"
            data-testid={`day-action-bar-${row.employee_id}`}>
            <p className="text-[11px] font-semibold text-slate-700 flex items-center gap-1.5">
              <MousePointerClick className="w-3.5 h-3.5 text-[#DC2626]" />
              HR decision — {selected.size} day(s) selected
              {selected.size > 0 && (
                <button type="button" className="ml-1 underline text-slate-500 font-normal"
                  onClick={() => setSelected(new Set())} data-testid={`clear-selection-${row.employee_id}`}>
                  clear selection
                </button>
              )}
            </p>
            {locked ? (
              <p className="text-[11px] text-slate-500 flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> Unlock the month to change any day.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  {ACTIONS.map(a => (
                    <Button key={a.key} size="sm" variant="outline" disabled={busy || !selected.size}
                      onClick={() => run(a.key)} className={`h-7 text-[11px] ${a.cls}`}
                      data-testid={`day-action-${a.key}-${row.employee_id}`}>
                      {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}{a.label}
                    </Button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] text-slate-500">Leave deducts from</span>
                  <Select value={leaveType} onValueChange={setLeaveType}>
                    <SelectTrigger className="h-7 w-52 text-[11px]" data-testid={`day-action-leave-type-${row.employee_id}`}>
                      <SelectValue placeholder="Leave type" />
                    </SelectTrigger>
                    <SelectContent>
                      {(leaveTypes.length ? leaveTypes : [{ leave_type: 'Casual Leave', balance: '—' }]).map(b => (
                        <SelectItem key={b.leave_type} value={b.leave_type}>
                          {b.leave_type} {b.balance !== undefined ? `(bal ${b.balance})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Reason / note (optional)" className="h-7 w-64 text-[11px]"
                    data-testid={`day-action-reason-${row.employee_id}`} />
                </div>
                <p className="text-[10px] text-slate-500">
                  Full-day Present = day worked, no leave deducted · Full-day Leave (paid) = 1 day off
                  the chosen balance · ½ Present + ½ Leave = 0.5 worked + 0.5 paid leave · ½ Present + ½
                  LWP = 0.5 worked + 0.5 unpaid · ½ Leave + ½ LWP = 0.5 paid leave + 0.5 unpaid · Full-day
                  LWP and Full-day Absent stay out of net payable days · Holiday and Weekly Off apply to
                  the whole institute · Revert to system undoes an earlier manual decision and gives the
                  leave balance back.
                </p>
              </>
            )}
          </div>

          <div>
            <Button size="sm" variant="ghost" className="h-7 text-[11px]"
              onClick={() => setShowDays((s) => !s)} data-testid={`day-grid-toggle-${row.employee_id}`}>
              <CalendarDays className="w-3 h-3 mr-1" />
              {showDays ? 'Hide' : 'View'} day-by-day
            </Button>
            {showDays && (
              <div className="mt-2 space-y-2" data-testid={`day-grid-${row.employee_id}`}>
                <div className="flex flex-wrap gap-1">
                  {codes.map((c, i) => {
                    const iso = `${month}-${String(i + 1).padStart(2, '0')}`;
                    const on = selected.has(iso);
                    return (
                      <button key={iso} type="button" onClick={() => toggle(iso)}
                        data-testid={`day-cell-${iso}`} aria-pressed={on}
                        title={`${iso} → ${c}${manual.has(iso) ? ' (decided by HR)' : ''} — click to select`}
                        className={`w-11 rounded border text-center py-0.5 text-[9px] font-semibold transition-all ${
                          CODE_STYLE[c.split('+')[0]] || 'bg-white border-slate-200'
                        } ${on ? 'ring-2 ring-[#DC2626] ring-offset-1' : 'hover:opacity-80'}`}>
                        <div className="text-[9px] opacity-70">
                          {i + 1}{manual.has(iso) ? ' •' : ''}
                        </div>
                        <div>{c}</div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-500">
                  Tap any day to select it, then pick an action above (choose <strong>Revert to
                  system</strong> to undo an HR decision). P Present · HP Half Present ·
                  L Paid Leave · HL Half Leave · LWP Leave Without Pay · WO Weekly Off · HO Holiday ·
                  A Absent · <strong>—</strong> Not in service (before joining / after leaving) ·
                  <strong>•</strong> decided by HR
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default PayableTallyPanel;
